"use client";

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchDomeById, updateDome } from "../../api/dome.api";

const EditDome = () => {
  const { domeid: id } = useParams();
  const navigate = useNavigate();

  const [domeData, setDomeData] = useState({
    domeId: "",
    domeName: "",
    domeDescription: "",
    price: "",
    offerPercent: 0,
    location: "",
  });

  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  const [coverImage, setCoverImage] = useState(null); // for existing cover image URL
  const [newCoverImage, setNewCoverImage] = useState(null); // for new uploaded cover file
  const [coverPreview, setCoverPreview] = useState(null); // preview URL for new cover

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ---------------- Fetch Dome Data ---------------- */
  useEffect(() => {
    const loadDome = async () => {
      try {
        setLoading(true);
        const data = await fetchDomeById(id);
        setDomeData({
          domeId: data.domeId || "",
          domeName: data.domeName || "",
          domeDescription: data.domeDescription || "",
          price: data.price || "",
          offerPercent: data.offerPercent || 0,
          location: data.location || "",
        });
        setExistingImages(data.images || []);
        setCoverImage(data.coverImage || null);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch dome details");
      } finally {
        setLoading(false);
      }
    };
    loadDome();
  }, [id]);

  /* ---------------- Handlers ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setDomeData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setNewImages((prev) => [...prev, ...files]);
  };

  const handleRemoveExistingImage = (url) => {
    setExistingImages((prev) => prev.filter((img) => img !== url));
  };

  const handleRemoveNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewCoverImage(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  /* ---------------- Preview URLs ---------------- */
  useEffect(() => {
    const urls = newImages.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setPreviewUrls(urls);

    return () => urls.forEach(({ url }) => URL.revokeObjectURL(url));
  }, [newImages]);

  /* ---------------- Submit ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      Object.entries(domeData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Include existing images (as URLs)
      existingImages.forEach((url) => formData.append("images", url));

      // Include new uploaded files
      newImages.forEach((file) => formData.append("images", file));

      // Include cover image (existing URL or new file)
      if (newCoverImage) {
        formData.append("coverImage", newCoverImage);
      } else if (coverImage) {
        formData.append("coverImage", coverImage);
      }

      await updateDome(id, formData);
      navigate("/hosts");
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to update dome");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <h2>
        Edit Dome:{" "}
        <span className="text-primary">
          {domeData.domeName || "Loading..."}
        </span>
      </h2>

      {loading ? (
        <p>Loading dome details...</p>
      ) : (
        <form onSubmit={handleSubmit} className="mt-3">
          {/* Dome ID */}
          <div className="mb-3">
            <label className="form-label">Dome ID</label>
            <input
              type="text"
              name="domeId"
              value={domeData.domeId}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>

          {/* Dome Name */}
          <div className="mb-3">
            <label className="form-label">Dome Name</label>
            <input
              type="text"
              name="domeName"
              value={domeData.domeName}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>

          {/* Dome Description */}
          <div className="mb-3">
            <label className="form-label">Dome Description</label>
            <textarea
              name="domeDescription"
              value={domeData.domeDescription}
              onChange={handleChange}
              className="form-control"
              rows={3}
            />
          </div>

          {/* Price & Offer */}
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Price</label>
              <input
                type="number"
                name="price"
                value={domeData.price}
                onChange={handleChange}
                className="form-control"
                min={0}
                required
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">Offer Percent</label>
              <input
                type="number"
                name="offerPercent"
                value={domeData.offerPercent}
                onChange={handleChange}
                className="form-control"
                min={0}
                max={100}
              />
            </div>
          </div>

          {/* Location */}
          <div className="mb-3">
            <label className="form-label">Location</label>
            <input
              type="text"
              name="location"
              value={domeData.location}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>

          {/* Cover Image */}
          <div className="mb-3">
            <label className="form-label">Cover Image</label>
            {coverPreview ? (
              <img
                src={coverPreview}
                alt="Cover Preview"
                className="img-fluid mb-2 rounded"
                style={{ maxHeight: 200 }}
              />
            ) : coverImage ? (
              <img
                src={coverImage}
                alt="Existing Cover"
                className="img-fluid mb-2 rounded"
                style={{ maxHeight: 200 }}
              />
            ) : null}
            <input
              type="file"
              accept="image/*"
              onChange={handleCoverChange}
              className="form-control"
            />
          </div>

          {/* Existing Images */}
          {existingImages.length > 0 && (
            <div className="mb-3">
              <label className="form-label">Existing Images</label>
              <div className="mt-2 d-flex flex-wrap gap-3">
                {existingImages.map((url, idx) => (
                  <div
                    key={idx}
                    className="border rounded position-relative overflow-hidden"
                    style={{
                      width: "120px",
                      height: "100px",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    <img
                      src={url}
                      alt={`existing-${idx}`}
                      className="img-fluid w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 px-2 py-0"
                      style={{
                        borderRadius: "50%",
                        fontSize: "0.8rem",
                        lineHeight: "1rem",
                      }}
                      onClick={() => handleRemoveExistingImage(url)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload New Images */}
          <div className="mb-3">
            <label className="form-label">Add New Images</label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileChange}
              className="form-control"
            />
            {previewUrls.length > 0 && (
              <div className="mt-3 d-flex flex-wrap gap-3">
                {previewUrls.map(({ url }, idx) => (
                  <div
                    key={idx}
                    className="border rounded position-relative overflow-hidden"
                    style={{
                      width: "120px",
                      height: "100px",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    <img
                      src={url}
                      alt={`preview-${idx}`}
                      className="img-fluid w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 px-2 py-0"
                      style={{
                        borderRadius: "50%",
                        fontSize: "0.8rem",
                        lineHeight: "1rem",
                      }}
                      onClick={() => handleRemoveNewImage(idx)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Updating..." : "Update Dome"}
          </button>
        </form>
      )}
    </div>
  );
};

export default EditDome;
